/**
 * Client-side image resizing using canvas.
 * Reduces large images before upload to save bandwidth and speed up transfers.
 * The backend still does canonical processing (sharp) after upload.
 */

const MAX_DIMENSION = 2048;
const MAX_BYTES = 4 * 1024 * 1024; // 4MB target

const RESIZABLE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
]);

/**
 * Resizes an image file if it exceeds dimension or byte thresholds.
 * Returns the original file unchanged for non-resizable types (SVG, GIF, etc.)
 * or images already within limits.
 */
export async function resizeImageBeforeUpload(file: File): Promise<File> {
  if (!RESIZABLE_TYPES.has(file.type)) {
    return file;
  }

  // Skip if already small enough in both dimensions and bytes
  if (file.size <= MAX_BYTES) {
    // Still need to check dimensions — load the image
    const dimensions = await getImageDimensions(file);
    if (
      dimensions.width <= MAX_DIMENSION &&
      dimensions.height <= MAX_DIMENSION
    ) {
      return file;
    }
  }

  // Load into an image element
  const img = await loadImage(file);

  // Calculate target dimensions (preserve aspect ratio)
  let { width, height } = img;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  // Draw to canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, width, height);

  // Export as the same type with progressive quality reduction if needed
  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const quality = 0.85;
  let blob = await canvasToBlob(canvas, outputType, quality);

  // If still over size limit, reduce quality (only for lossy formats)
  if (outputType !== 'image/png') {
    const qualitySteps = [0.75, 0.65, 0.5, 0.4];
    for (const q of qualitySteps) {
      if (blob.size <= MAX_BYTES) break;
      blob = await canvasToBlob(canvas, outputType, q);
    }
  }

  // Return as a new File with the original name
  const extension = outputType === 'image/png' ? '.png' : '.jpg';
  const baseName = file.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}${extension}`, { type: outputType });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null'));
      },
      type,
      quality,
    );
  });
}
